import { forwardRef, type RefObject, useRef, type HTMLInputAutoCompleteAttribute, type KeyboardEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import { useResizeObserver } from '@shared/hooks';
import { PaletteUtils } from '@shared/utils';

const MB_6 = 24;
const LINE_HEIGHT_ERROR = 20;

interface InputProps {
  // COMMON PROPS
  className?: string;
  inputClassName?: string;
  captionClassName?: string;
  caption?: ReactNode;
  error?: ReactNode;
  inputPattern?: RegExp | string;
  formStyle?: boolean;
  // BASE INPUT PROPS
  name?: string;
  type?: 'text' | 'number' | 'password';
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  autoComplete?: HTMLInputAutoCompleteAttribute;
  value?: string | number | readonly string[];
  onChange?: (value: string) => void;
}

export const TextInput = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const errorRef = useRef<HTMLDivElement>(null);
  const { height } = useResizeObserver({ ref: errorRef as RefObject<HTMLElement>, box: 'border-box' });

  const keyDownHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.altKey) return;

    if (props.inputPattern) {
      if (e.key && e.key.length === 1) {
        if (!e.key.match(props.inputPattern)) e.preventDefault();
      }
    }
  };

  const mb = MB_6 + ((height || 0) - LINE_HEIGHT_ERROR);
  return (
    <div className={clsx(`mb-6 relative`, height && `!mb-[${mb}px]`, props.className)} style={{ marginBottom: `${height ? mb : MB_6}px` }}>
      <div className='mb-1.5'>
        <label
          className={clsx(
            'text-xl tracking-[0.1px] leading-[25px]',
            !props.formStyle && (PaletteUtils.isBordierPalette ? 'text-[#303034] font-medium' : 'text-[#dadada] font-light'),
            props.formStyle && (PaletteUtils.isBordierPalette ? '' : 'text-[#4c4c52] font-bold mb-2'),
          )}
        >
          {props.required && <span className='pr-1'>*</span>}
          {props.caption}
        </label>
      </div>
      <input
        ref={ref}
        className={clsx(
          'transaction-all duration-300',
          props.formStyle
            ? 'w-full !h-10 px-[15px] tracking-[0.3px]  border border-[#aaaab2] shadow-[inset_0_2px_6px_0_rgba(85,85,90,0.39)] text-lg font-semibold'
            : 'w-full h-[50px] px-[15px] text-[18px] font-semibold tracking-[0.3px]  border  focus:border-[#979797] focus:outline-none',
          !props.formStyle &&
            (!PaletteUtils.isBordierPalette
              ? 'bg-[#303034] !border-[#6d6d75] text-[#dadada] hover:!border-[#9f9fa5] focus:!border-[#9f9fa5]'
              : 'text-[#4c4c52] bg-[#f8f8f8] border-[#e1e1e1] hover:border-[#979797] focus:border-[#979797]'),
          {
            '!hover:border-[#979797]': !props.error,
            '!opacity-50 !cursor-not-allowed': props.disabled || props.readOnly,
          },
          !!props.error &&
            (PaletteUtils.isBordierPalette
              ? '!border-[#d34014] hover:!border-[#d34014] focus:!border-[#d34014]'
              : '!border-[#d44112] hover:!border-[#d44112] focus:!border-[#d44112]'),
          !!props.error &&
            props.formStyle &&
            (PaletteUtils.isBordierPalette ? ' shadow-[inset_0_2px_6px_0_rgba(212,65,18,0.39)]' : ' shadow-[inset_0_2px_6px_0_rgba(212,65,18,0.39)]'),
          props.className,
        )}
        name={props.name}
        type={props.type}
        placeholder={props.placeholder}
        required={props.required}
        disabled={props.disabled}
        readOnly={props.readOnly}
        pattern={props.pattern}
        minLength={props.minLength}
        maxLength={props.maxLength}
        autoComplete={props.autoComplete}
        value={props.value ?? ''}
        onChange={(e) => props.onChange?.(e.target.value)}
        onKeyDown={keyDownHandler}
      />
      <div
        ref={errorRef}
        className={clsx(
          'hidden left-0 mt-0 top-full text-[#d44112] tracking-[-0.1px] text-left whitespace-pre-line',
          props.error && '!block',
          props.formStyle && 'text-base font-normal absolute top-0',
          !props.formStyle && 'text-lg mt-[9px] leading-6',
        )}
      >
        {props.error}
      </div>
    </div>
  );
});
